import React, { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Order, OrderBundleItem } from '../../../store/order/api/interface';
import { useAppSelector } from '../../../store';
import { resolveAssetUrl } from '../../../utils/url';
import OrderBundleExtrasCollapseRows from './OrderBundleExtrasCollapseRows';
import {
  getBundleLineTotal,
  getOrderSpecialRequestText,
} from '../../../utils/orderTotals';

interface Props {
  bundleItem: OrderBundleItem;
  order: Order;
  idx: number;
  showSpecialRequest?: boolean;
  handleOpenLightBox?: (imageObject: string) => void;
  canRemove?: boolean;
  removing?: boolean;
  onRemove?: (bundleItem: OrderBundleItem) => Promise<void> | void;
}

const OrderBundleItemTableRow = ({
  bundleItem,
  order,
  idx,
  showSpecialRequest = false,
  handleOpenLightBox,
  canRemove = false,
  removing = false,
  onRemove,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );
  const hasChildren =
    (bundleItem.components?.length ?? 0) > 0 ||
    (bundleItem.variantSelections?.length ?? 0) > 0;
  const bundleQty = Number(bundleItem.qty ?? 1) || 1;
  const bundleUnitPrice = parseFloat(
    Number(bundleItem.unitPrice ?? 0).toFixed(2)
  );
  const rowTotal = parseFloat(Number(getBundleLineTotal(bundleItem)).toFixed(2));
  const bundlePreviewKey = bundleItem.previewUrl || '';
  const bundlePreviewSrc = bundlePreviewKey
    ? resolveAssetUrl(bundlePreviewKey, assetUrl)
    : '';

  return (
    <>
      <TableRow
        sx={{
          borderBottom: (theme) => `solid 1px ${theme.palette.divider}`,
        }}
      >
        <TableCell align='left'>
          {hasChildren && (
            <IconButton
              aria-label='expand bundle row'
              size='small'
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{idx + 1}</TableCell>
        <TableCell align='left'>
          {bundlePreviewSrc ? (
            <Box
              component='img'
              src={bundlePreviewSrc}
              alt={bundleItem.labelEn || 'Bundle product'}
              onClick={
                handleOpenLightBox && bundlePreviewKey
                  ? () => handleOpenLightBox(bundlePreviewKey)
                  : undefined
              }
              sx={{
                width: 48,
                height: 48,
                m: 1,
                objectFit: 'contain',
                cursor: handleOpenLightBox ? 'pointer' : 'default',
                display: 'block',
              }}
            />
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell align='right'>-</TableCell>
        <TableCell align='right'>-</TableCell>
        <TableCell align='right'>-</TableCell>
        <TableCell align='right'>{bundleItem.labelEn || '-'}</TableCell>
        <TableCell align='right'>
          {showSpecialRequest ? getOrderSpecialRequestText(order) : '-'}
        </TableCell>
        <TableCell align='right'>-</TableCell>
        <TableCell align='right'>{bundleQty}</TableCell>
        <TableCell align='right'>{bundleUnitPrice}</TableCell>
        <TableCell align='right'>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.5,
            }}
          >
            <Typography component='span'>{rowTotal}</Typography>
            {canRemove ? (
              <Tooltip title='Remove package'>
                <span>
                  <IconButton
                    aria-label='remove package'
                    size='small'
                    color='error'
                    disabled={removing}
                    onClick={() => setConfirmOpen(true)}
                  >
                    <DeleteOutlineIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Box>
        </TableCell>
      </TableRow>
      {hasChildren && (
        <TableRow>
          <TableCell
            style={{ paddingBottom: 0, paddingTop: 0 }}
            colSpan={12}
          >
            <Collapse
              in={open}
              timeout='auto'
              unmountOnExit
            >
              <Box sx={{ py: 1 }}>
                <Table
                  size='small'
                  aria-label={`bundle-items-${bundleItem.id}`}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>Type</TableCell>
                      <TableCell align='center'>Name</TableCell>
                      <TableCell align='center'>Image</TableCell>
                      <TableCell align='center'>Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <OrderBundleExtrasCollapseRows
                      order={order}
                      bundleItem={bundleItem}
                      hideBundleHeader
                      hideLineSubtotal
                      includeCakeComponents
                      handleOpenLightBox={handleOpenLightBox}
                    />
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
      <Dialog
        open={confirmOpen}
        onClose={removing ? undefined : () => setConfirmOpen(false)}
      >
        <DialogTitle>Remove package?</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            Remove <strong>{bundleItem.labelEn || 'this package'}</strong> from
            the order? Totals will update.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={removing}
          >
            Cancel
          </Button>
          <Button
            color='error'
            variant='contained'
            disabled={removing}
            onClick={async () => {
              await onRemove?.(bundleItem);
              setConfirmOpen(false);
            }}
          >
            {removing ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderBundleItemTableRow;
